/* global debounce */

/**
 * Exact search terms (lowercased, single-spaced) whose dropdown must lead with a given
 * product handle. Predictive search reads 'replay' as a typo of 'replacement', and
 * Search & Discovery boosts only reach the search results page, not this dropdown
 * (Shopify Support, 2026-10-20). Exact terms only: a prefix like 'repla' would push the
 * pin into genuine 'replacement' searches.
 */
const PINNED_RESULTS = {
  replay: 'rega-replay-turntable',
  'rega replay': 'rega-replay-turntable',
};

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.requestToken = 0;
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Builds the query string for one predictive search request.
     * @param {string} searchTerm - Search query.
     * @returns {string} Query string.
     */
    buildSearchParams(searchTerm) {
      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      return searchParams;
    }

    /**
     * Fetches one predictive search response and returns its section element.
     * @param {string} searchTerm - Search query.
     * @returns {Promise<Element|null>} Section element, or null if absent.
     */
    async fetchSection(searchTerm) {
      const response = await fetch(`${theme.routes.predictiveSearch}?${this.buildSearchParams(searchTerm)}`);
      if (!response.ok) throw new Error(response.status);

      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();
      return tmpl.content.querySelector('#shopify-section-predictive-search');
    }

    /**
     * Refills product slots that the discontinued filter emptied.
     *
     * Shopify spends its result limit BEFORE predictive-search-tab-panel.liquid skips
     * discontinued items, so a query matching a sunset brand arrives with most of its
     * slots already thrown away - 'preamplifier' rendered 3 of 10 because 7 were
     * discontinued Audio Research preamps, and 'audio research' rendered an empty
     * Products tab. The limit is hard-capped at 10, so there is no room to over-fetch.
     *
     * The primary request stays a plain query ON PURPOSE: appending search syntax turns
     * OFF Search & Discovery boosts, which silently reorders curated brand results
     * (measured 2026-09-03 - 'magnepan' led with the 1.7x plain and with the MC1 once
     * syntax was added). So the boosted request goes first and a second, syntax-bearing
     * request only supplies replacements for what was dropped.
     * @param {Element} resultsEl - Section element from the primary request.
     * @param {string} searchTerm - Search query.
     * @param {number} token - Request token, so a stale response is discarded.
     */
    async backfillFilteredProducts(resultsEl, searchTerm, token) {
      const wrapper = resultsEl.querySelector('[data-products-returned]');
      const list = resultsEl.querySelector('#psearch-products .predictive-search__list');
      if (!wrapper || !list) return;

      const returned = parseInt(wrapper.dataset.productsReturned, 10) || 0;
      const shown = list.querySelectorAll('.predictive-search__item:not(.js-submit)').length;
      const limit = parseInt(theme.settings.pSearchLimit, 10) || 10;
      if (returned <= shown || shown >= limit) return;

      // A failed backfill must never cost the customer the results we already have:
      // fetchSection throws on a non-OK response, and letting that propagate would hit
      // getResults' catch and close the whole dropdown - worse than the bug this fixes,
      // and worst on exactly the queries it targets. Degrade to the primary results.
      let backfillEl = null;
      try {
        backfillEl = await this.fetchSection(`${searchTerm} -tag:discontinued`);
      } catch (error) {
        return;
      }
      if (!backfillEl || token !== this.requestToken) return;

      const seen = new Set(
        Array.from(list.querySelectorAll('a[href]')).map((link) => link.getAttribute('href'))
      );
      // The 'View all search results' item is the last child of this same list, so
      // replacements are inserted before it rather than appended after it.
      const viewAll = list.querySelector('.js-submit');
      let slots = limit - shown;

      backfillEl
        .querySelectorAll('#psearch-products .predictive-search__item:not(.js-submit)')
        .forEach((item) => {
          if (slots <= 0) return;
          const link = item.querySelector('a[href]');
          const href = link && link.getAttribute('href');
          if (!href || seen.has(href)) return;

          seen.add(href);
          list.insertBefore(item.cloneNode(true), viewAll);
          slots -= 1;
        });
    }

    /**
     * Puts a pinned product first for the exact terms in PINNED_RESULTS.
     *
     * The pinned item is fetched with a second request for the handle's words ('rega
     * replay turntable'), which Shopify ranks first, and its rendered item is cloned to
     * the top of the list. Every other query returns before any request is made. The
     * primary request stays plain so Search & Discovery boosts keep working on it.
     * @param {Element} resultsEl - Section element from the primary request.
     * @param {string} searchTerm - Search query.
     * @param {number} token - Request token, so a stale response is discarded.
     */
    async pinProducts(resultsEl, searchTerm, token) {
      const handle = PINNED_RESULTS[searchTerm.toLowerCase().replace(/\s+/g, ' ').trim()];
      if (!handle) return;

      const list = resultsEl.querySelector('#psearch-products .predictive-search__list');
      if (!list) return;

      const isPinned = (item) => {
        const link = item.querySelector('a[href]');
        return !!link && link.getAttribute('href').split('?')[0].endsWith(`/products/${handle}`);
      };
      const items = () => Array.from(list.querySelectorAll('.predictive-search__item:not(.js-submit)'));

      let pinned = items().find(isPinned);
      if (!pinned) {
        // Same rule as the backfill: a failed request must never cost the customer the
        // results we already have.
        let pinEl = null;
        try {
          pinEl = await this.fetchSection(handle.replace(/-/g, ' '));
        } catch (error) {
          return;
        }
        if (!pinEl || token !== this.requestToken) return;

        const found = Array.from(
          pinEl.querySelectorAll('#psearch-products .predictive-search__item:not(.js-submit)')
        ).find(isPinned);
        if (!found) return;
        pinned = found.cloneNode(true);
      }

      list.insertBefore(pinned, list.firstElementChild);

      // Stay within the limit by dropping the last product; 'View all' stays last.
      const limit = parseInt(theme.settings.pSearchLimit, 10) || 10;
      const all = items();
      if (all.length > limit) all[all.length - 1].remove();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      const queryKey = this.buildSearchParams(searchTerm).replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      const token = (this.requestToken += 1);

      try {
        const resultsEl = await this.fetchSection(searchTerm);
        if (token !== this.requestToken) return;
        if (!resultsEl) return;

        await this.backfillFilteredProducts(resultsEl, searchTerm, token);
        if (token !== this.requestToken) return;

        await this.pinProducts(resultsEl, searchTerm, token);
        if (token !== this.requestToken) return;

        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
